/**
 * Content store for the single shared `ReactTooltip` instance (see `AppTooltip.component`).
 *
 * react-tooltip v4 is a singleton-per-page library: `id`/`data-for` exist so that *many*
 * triggers can share *one* instance. Rendering an instance per trigger costs, per instance,
 * a full-document element walk (`getTargetArray` does `document.getElementsByTagName('*')`)
 * plus a document-wide `MutationObserver` (`bindRemovalTracker`, which has no opt-out) plus
 * a `keydown`/`resize`/rebuild window listener. Screens that render a tooltip per table cell
 * ended up with hundreds of those, which made every DOM mutation and every keystroke walk
 * the whole app.
 *
 * So instead: each `<Tooltip>` registers its content here under its own id, its trigger
 * points `data-for` at the shared instance and carries its own id in `data-tip`, and the
 * shared instance resolves the content lazily on hover.
 */
const contentById = new Map();

let contentListener = null;

let removalListener = null;

/**
 * Lets the shared instance react to content that shows up after a tooltip was already hovered
 * (see `AppTooltip`). Returns an unsubscribe function.
 */
export function observeTooltipContent(listener) {
    contentListener = listener;

    return () => {
        if (contentListener === listener) {
            contentListener = null;
        }
    };
}

/**
 * Lets the shared instance dismiss a bubble whose owner is going away (see `AppTooltip`).
 * Returns an unsubscribe function.
 */
export function observeTooltipRemoval(listener) {
    removalListener = listener;

    return () => {
        if (removalListener === listener) {
            removalListener = null;
        }
    };
}

export function setTooltipContent(id, content) {
    if (id) {
        contentById.set(id, content);
        contentListener?.(id);
    }
}

/**
 * `content` guards against a tooltip that re-registered the same id from another component
 * losing its entry when the first one unmounts.
 */
export function deleteTooltipContent(id, content) {
    if (id && contentById.get(id) === content) {
        // Announced before the entry goes, so a listener can still hide a tooltip that is
        // showing this content: react-tooltip treats an empty tip as a disabled one and
        // returns from `hideTooltip` without hiding anything.
        removalListener?.(id);
        contentById.delete(id);
    }
}

/**
 * Resolve what the shared instance should render for the trigger currently being hovered.
 * `id` is that trigger's `data-tip` value, handed to us by react-tooltip's `getContent`.
 *
 * Must return `null` and never `undefined` for an unknown id: react-tooltip's `TipContent`
 * falls back to rendering the raw `data-tip` string when `getContent` returns `undefined`,
 * which would show the id itself, whereas `null` counts as an empty tip and stays hidden.
 */
export function getTooltipContent(id) {
    const content = contentById.get(id);

    if (content === undefined) {
        return null;
    }

    const resolved = typeof content === "function" ? content() : content;

    return resolved === undefined ? null : resolved;
}